import React, { useEffect, useState } from "react";
import Modals from "./components/ModalAddUser";
import CardUsers from "./components/CardUsers";
import { MdCheckCircle, MdGroup, MdPersonOutline } from "react-icons/md";
import reviewersService from "../../../service/reviewers.service";
import AffiliationService from "../../../service/affiliation.service";
import Swal from "sweetalert2";
import Select from "react-select";

const Utilisateurs = () => {
  // Pagination state
  const [reviewers, setReviewers] = useState([]);
  const [affiliations, setAffiliations] = useState([]);
  const [selectedAffiliations, setSelectedAffiliations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null); // Changé en objet unique
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentReviewer, setCurrentReviewer] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [nameWarning, setNameWarning] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  

  // Récupérer les affiliations au montage
  useEffect(() => {
    const fetchAffiliations = async () => {
      try {
        const res = await AffiliationService.getAll({
          page: 1,
          limit: 10000,
          order_by: "name_aff",
          order_direction: "asc"
        });
        console.log(res);
        if (res?.success && res?.data?.data) {
          setAffiliations(
            res.data.data.map((aff) => ({
              value: aff.id_aff,
              label: aff.name_aff,
            }))
          );
        } else {
          setAffiliations([]);
        }
      } catch (error) {
        console.error("Erreur lors du chargement des affiliations:", error);
        setAffiliations([]);
        
        Swal.fire({
          icon: "warning",
          title: "Attention",
          text: "Impossible de charger les affiliations. Vous pourrez toujours créer un utilisateur, mais sans affiliation.",
          confirmButtonColor: "#57A863",
          toast: true,
          position: "top-end",
          timer: 5000,
          showConfirmButton: false,
        });
      }
    };

    const fetchReviewerCategories = async () => {
      try {
        const res = await reviewersService.getAllReviewerCategories(
          1,
          10,
          "",
          "id_cat_rev"
        );
        console.log("Categories response:", res);
        if (res?.success && res?.data) {
          setCategories(
            res.data.map((cat) => ({
              value: cat.id_cat_rev,
              label: cat.name_cat_rev,
            }))
          );
        } else {
          setCategories([]);
        }
      } catch (error) {
        console.error("Erreur lors du chargement des catégories :", error);
        setCategories([]);
        
        Swal.fire({
          icon: "warning",
          title: "Attention",
          text: "Impossible de charger les catégories. Vous pourrez toujours créer un utilisateur, mais sans catégorie.",
          confirmButtonColor: "#57A863",
          toast: true,
          position: "top-end",
          timer: 5000,
          showConfirmButton: false,
        });
      }
    };

    fetchAffiliations();
    fetchReviewerCategories();
  }, []);

  // Récupérer les reviewers paginés
  const fetchReviewers = async (page = currentPage, limit = perPage) => {
    setIsLoading(true);
    try {
      const response = await reviewersService.getAll(page, limit);
      setReviewers(response.data?.data || []);
      setCurrentPage(response.data?.current_page || page);
      setLastPage(response.data?.last_page || 1);
      setPerPage(Number(response.data?.per_page) || limit);
      setTotal(response.data?.total || 0);
    } catch (error) {
      setReviewers([]);
      Swal.fire({
        icon: "error",
        title: "Erreur",
        text: "Impossible de charger les rélecteurs. Veuillez réessayer plus tard.",
        confirmButtonColor: "#57A863",
      });
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchReviewers(1, perPage);
    // eslint-disable-next-line
  }, []);

  // Callback pour la pagination du composant CardUsers
  const handleFetchPage = (page, limit) => {
    fetchReviewers(page, limit);
  };

  // Vérification des doublons de nom
  const checkDuplicateName = (
    firstName,
    lastName,
    currentReviewerId = null
  ) => {
    return reviewers.some(
      (reviewer) =>
        reviewer.first_name?.toLowerCase() === firstName?.toLowerCase() &&
        reviewer.last_name?.toLowerCase() === lastName?.toLowerCase() &&
        reviewer.id_rev !== currentReviewerId
    );
  };

  // Vérification des doublons d'email
  const checkDuplicateEmail = (email, currentReviewerId = null) => {
    return reviewers.some(
      (reviewer) =>
        reviewer.email_user?.toLowerCase() === email?.toLowerCase() &&
        reviewer.id_rev !== currentReviewerId
    );
  };

  // Gestion des changements de nom en temps réel
  const handleNameChange = (firstName, lastName) => {
    if (
      firstName &&
      lastName &&
      checkDuplicateName(firstName, lastName, currentReviewer?.id_rev)
    ) {
      setNameWarning(
        `Un utilisateur avec le nom "${firstName} ${lastName}" existe déjà.`
      );
    } else {
      setNameWarning("");
    }
  };

  const handleNew = () => {
    setCurrentReviewer(null);
    setIsEditMode(false);
    setIsOpen(true);
    setSelectedAffiliations([]);
    setSelectedCategory(null); // Réinitialiser la catégorie
    setNameWarning("");
  };

  const handleEdit = (reviewer) => {
    setCurrentReviewer(reviewer);
    setIsEditMode(true);
    setIsOpen(true);
    setNameWarning("");

    // Mapper les affiliations du rélecteur vers le format Select
    if (
      reviewer.affiliation_managers &&
      Array.isArray(reviewer.affiliation_managers)
    ) {
      const mappedAffiliations = reviewer.affiliation_managers
        .filter((am) => am.affiliation)
        .map((am) => ({
          value: am.affiliation.id_aff,
          label: am.affiliation.name_aff,
        }));
      setSelectedAffiliations(mappedAffiliations);
    } else {
      setSelectedAffiliations([]);
    }

    // Mapper la catégorie du rélecteur vers le format Select
    if (reviewer.id_cat_rev && categories.length > 0) {
      const mappedCategory = categories.find(
        (cat) => cat.value === reviewer.id_cat_rev
      );
      setSelectedCategory(mappedCategory || null);
    } else {
      setSelectedCategory(null);
    }
  };

  // Mettre à jour la catégorie sélectionnée quand les catégories sont chargées
  useEffect(() => {
    if (isEditMode && currentReviewer?.id_cat_rev && categories.length > 0) {
      const mappedCategory = categories.find(
        (cat) => cat.value === currentReviewer.id_cat_rev
      );
      setSelectedCategory(mappedCategory || null);
    }
  }, [categories, isEditMode, currentReviewer]);

  const handleClose = () => {
    setIsOpen(false);
    setCurrentReviewer(null);
    setIsEditMode(false);
    setSelectedAffiliations([]);
    setSelectedCategory(null); // Réinitialiser la catégorie
    setNameWarning("");
  };

  const handleSaveReviewer = async (formData) => {
    setIsSubmitting(true);

    try {
      const reviewerData = {
        first_name: formData.get("first_name"),
        last_name: formData.get("last_name"),
        email_user: formData.get("email_user"),
        id_cat_rev: selectedCategory?.value || null, // Utiliser la catégorie sélectionnée
        id_aff: selectedAffiliations.map((aff) => aff.value),
      };

      const idRev = formData.get("id_rev");

      let response;
      if (isEditMode) {
        response = await reviewersService.update(idRev, reviewerData);
      } else {
        response = await reviewersService.create(reviewerData);
      }

      if (response && response.success) {
        fetchReviewers();
        Swal.fire({
          icon: "success",
          title: "Succès",
          text: isEditMode
            ? "Rélecteur mis à jour avec succès!"
            : "Rélecteur ajouté avec succès!",
          confirmButtonColor: "#57A863",
        });
        handleClose();
      } else {
        let errorMessage =
          response?.message || response?.data || "Erreur inconnue";

        if (typeof errorMessage === "object" && errorMessage.message) {
          errorMessage = errorMessage.message;
        }

        throw new Error(errorMessage);
      }
    } catch (error) {
      let errorTitle = "Erreur";
      let errorMessage = error.message || "Une erreur est survenue";
      let errorIcon = "error";

      if (error.response?.status === 409 || error.message?.includes("409")) {
        errorTitle = "Conflit de données";
        errorIcon = "warning";

        const errorData = error.response?.data;
        let conflictMessage = "";
        let solutions = [];

        if (errorData?.message) {
          const msg =
            typeof errorData.message === "object"
              ? errorData.message.message
              : errorData.message;

          if (msg.includes("email") || msg.includes("e-mail")) {
            conflictMessage =
              "Un utilisateur avec cette adresse email existe déjà.";
            solutions = [
              "Vérifiez l'orthographe de l'adresse email",
              "Utilisez une adresse email différente",
              "Vérifiez si cet utilisateur existe déjà dans la liste",
            ];
          } else if (
            msg.includes("nom") ||
            msg.includes("name") ||
            msg.includes("prénom")
          ) {
            conflictMessage =
              "Un utilisateur avec ce nom et prénom existe déjà.";
            solutions = [
              "Ajoutez un deuxième prénom ou une initiale",
              "Vérifiez si cette personne existe déjà",
              "Contactez l'administrateur si c'est une personne différente",
            ];
          } else {
            conflictMessage =
              "Les données saisies entrent en conflit avec un enregistrement existant.";
            solutions = [
              "Vérifiez l'unicité de toutes les informations",
              "Modifiez les données en conflit",
              "Contactez l'administrateur si le problème persiste",
            ];
          }
        } else {
          conflictMessage = "Un conflit de données a été détecté.";
          solutions = [
            "Vérifiez que l'email n'est pas déjà utilisé",
            "Vérifiez que le nom/prénom n'existe pas déjà",
            "Réessayez avec des informations différentes",
          ];
        }

        Swal.fire({
          icon: errorIcon,
          title: errorTitle,
          html: `
            <div class="text-left space-y-4">
              <div class="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p class="text-amber-800 font-medium">⚠️ ${conflictMessage}</p>
              </div>
              
              <div>
                <p class="font-semibold text-gray-700 mb-2">Solutions recommandées :</p>
                <ul class="list-disc list-inside text-sm text-gray-600 space-y-1">
                  ${solutions
                    .map((solution) => `<li>${solution}</li>`)
                    .join("")}
                </ul>
              </div>
              
              <div class="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p class="text-blue-800 text-sm">
                  <strong>Astuce :</strong> Utilisez la fonction de recherche pour vérifier si l'utilisateur existe déjà.
                </p>
              </div>
            </div>
          `,
          confirmButtonText: "Compris",
          confirmButtonColor: "#57A863",
          showCloseButton: true,
          width: "600px",
          customClass: {
            container: "conflict-error-modal",
          },
        });

        return;
      }

      if (
        errorMessage.includes(
          "utilisateur avec ce prénom et nom existe déjà"
        ) ||
        errorMessage.includes("prénom et nom existe déjà")
      ) {
        errorTitle = "Utilisateur existant";
        errorIcon = "warning";

        Swal.fire({
          icon: errorIcon,
          title: errorTitle,
          html: `
            <div class="text-left space-y-3">
              <p><strong>Problème :</strong> Un utilisateur avec ce prénom et nom existe déjà dans le système.</p>
              <p><strong>Solutions possibles :</strong></p>
              <ul class="list-disc list-inside text-sm text-gray-600 space-y-1">
                <li>Vérifiez si cette personne existe déjà dans la liste</li>
                <li>Ajoutez un deuxième prénom ou initial (ex: Jean-Pierre, Jean P.)</li>
                <li>Contactez l'administrateur si c'est une personne différente</li>
                ${
                  isEditMode
                    ? "<li>Vérifiez que vous ne modifiez pas vers un nom déjà existant</li>"
                    : ""
                }
              </ul>
            </div>
          `,
          confirmButtonText: "Compris",
          confirmButtonColor: "#57A863",
          showCloseButton: true,
          width: "500px",
        });

        return;
      }

      if (errorMessage.includes("id_aut")) {
        errorMessage =
          "Erreur de configuration de la base de données. Contactez l'administrateur système.";

        console.error("ERREUR BASE DE DONNÉES - À corriger côté backend:", {
          error: error.message,
          solution:
            "Remplacer 'id_aut' par 'id_rev' dans la requête DELETE de la table affiliation_reviwer_managers",
          reviewer_id: formData.get("id_rev"),
          timestamp: new Date().toISOString(),
        });
      }

      Swal.fire({
        icon: errorIcon,
        title: errorTitle,
        text: `${
          isEditMode ? "Échec de la mise à jour" : "Échec de l'ajout"
        } du rélecteur: ${errorMessage}`,
        confirmButtonColor: "#d33",
        footer: errorMessage.includes("base de données")
          ? "<small>Code erreur: DB_CONFIG_ERROR</small>"
          : null,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);

    const firstName = formData.get("first_name");
    const lastName = formData.get("last_name");
    const email = formData.get("email_user");
    const currentId = isEditMode ? parseInt(formData.get("id_rev")) : null;

    let warnings = [];

    if (checkDuplicateName(firstName, lastName, currentId)) {
      warnings.push(`Le nom "${firstName} ${lastName}" existe déjà`);
    }

    if (checkDuplicateEmail(email, currentId)) {
      warnings.push(`L'email "${email}" est déjà utilisé`);
    }

    if (warnings.length > 0) {
      Swal.fire({
        icon: "warning",
        title: "Attention - Doublons détectés",
        html: `
          <div class="text-left">
            <p class="mb-3">Les éléments suivants existent déjà :</p>
            <ul class="list-disc list-inside text-sm text-red-600 space-y-1 mb-4">
              ${warnings.map((warning) => `<li>${warning}</li>`).join("")}
            </ul>
            <p class="text-sm text-gray-600">Voulez-vous continuer quand même ?</p>
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: "Continuer quand même",
        cancelButtonText: "Modifier les données",
        confirmButtonColor: "#d97706",
        cancelButtonColor: "#57A863",
      }).then((result) => {
        if (result.isConfirmed) {
          handleSaveReviewer(formData);
        }
      });
      return;
    }

    await handleSaveReviewer(formData);
  };

  const handleDeleteReviewer = async (reviewer) => {
    const result = await Swal.fire({
      title: "Êtes-vous sûr ?",
      html: `Voulez-vous vraiment supprimer <strong>${reviewer.first_name} ${reviewer.last_name}</strong> ?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Oui, supprimer",
      cancelButtonText: "Annuler",
    });

    if (result.isConfirmed) {
      try {
        Swal.fire({
          title: "Suppression en cours...",
          html: `
            <div class="flex flex-col items-center">
              <div class="relative w-16 h-16">
                <div class="absolute top-0 left-0 w-16 h-16 border-4 border-red-500 border-opacity-20 rounded-full"></div>
                <div class="absolute top-0 left-0 w-16 h-16 border-4 border-transparent border-t-red-500 rounded-full animate-spin"></div>
              </div>
              <p class="mt-4 text-gray-700">Suppression en cours...</p>
            </div>
          `,
          showConfirmButton: false,
          allowOutsideClick: false,
        });

        const response = await reviewersService.delete(reviewer.id_rev);
        Swal.close();

        if (response && response.success) {
          fetchReviewers(currentPage, perPage);
          Swal.fire({
            icon: "success",
            title: "Supprimé !",
            text: `${reviewer.first_name} ${reviewer.last_name} a été supprimé avec succès.`,
            confirmButtonColor: "#57A863",
          });
        } else {
          throw new Error(response?.message || "Échec de la suppression");
        }
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Erreur",
          text:
            error.message ||
            "Une erreur s'est produite lors de la suppression du rélecteur.",
          confirmButtonColor: "#d33",
        });
      }
    }
  };

  const handleViewDetails = (reviewer) => {
    const affiliationsList =
      reviewer.affiliation_managers
        ?.map(
          (am) =>
            `• ${am.affiliation?.name_aff} (${am.affiliation?.country_aff}) - ${am.affiliation?.type_aff}`
        )
        .join("<br>") || "Aucune affiliation";

    Swal.fire({
      title: `${reviewer.first_name} ${reviewer.last_name}`,
      html: `
        <div class="text-left space-y-3">
          <div>
            <strong>Email:</strong> ${reviewer.email_user}
          </div>
          <div>
            <strong>Catégorie:</strong> ${reviewer.category?.name_cat_rev}
          </div>
          <div>
            <strong>Créé le:</strong> ${new Date(
              reviewer.created_at
            ).toLocaleDateString("fr-FR", {
              year: "numeric",
              month: "long",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </div>
          <div>
            <strong>Dernière mise à jour:</strong> ${new Date(
              reviewer.updated_at
            ).toLocaleDateString("fr-FR", {
              year: "numeric",
              month: "long",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </div>
          <div>
            <strong>Affiliations (${
              reviewer.affiliation_managers?.length || 0
            }):</strong>
            <div class="mt-2 text-sm text-gray-600">
              ${affiliationsList}
            </div>
          </div>
        </div>
      `,
      showCloseButton: true,
      confirmButtonText: "Fermer",
      confirmButtonColor: "#57A863",
      width: "600px",
    });
  };

  return (
    <div className="mt-5 flex h-full">
      <CardUsers
        isOpen={handleNew}
        users={reviewers}
        isLoading={isLoading}
        onEdit={handleEdit}
        onDelete={handleDeleteReviewer}
        onViewDetails={handleViewDetails}
        currentPage={currentPage}
        perPage={perPage}
        lastPage={lastPage}
        total={total}
        fetchPage={handleFetchPage}
      />

      <Modals
        isOpen={isOpen}
        onClose={handleClose}
        width="64"
        title={isEditMode ? "Modifier un rélecteur" : "Ajouter un rélecteur"}
      >
        <form onSubmit={handleSubmit}>
          <input
            type="hidden"
            name="id_rev"
            value={currentReviewer?.id_rev || ""}
          />

          <div className="mb-4">
            <label
              htmlFor="first_name"
              className="mb-2 block text-sm font-medium"
            >
              Nom *
            </label>
            <input
              type="text"
              name="first_name"
              id="first_name"
              placeholder="Nom du rélecteur"
              className="focus:border-transparent w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#57A863]"
              defaultValue={currentReviewer?.first_name || ""}
              onChange={(e) => {
                const lastName =
                  document.getElementById("last_name")?.value || "";
                handleNameChange(e.target.value, lastName);
              }}
              required
            />
          </div>

          <div className="mb-4">
            <label
              htmlFor="last_name"
              className="mb-2 block text-sm font-medium"
            >
              Prénoms *
            </label>
            <input
              type="text"
              name="last_name"
              id="last_name"
              placeholder="Prénoms du rélecteur"
              className="focus:border-transparent w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#57A863]"
              defaultValue={currentReviewer?.last_name || ""}
              onChange={(e) => {
                const firstName =
                  document.getElementById("first_name")?.value || "";
                handleNameChange(firstName, e.target.value);
              }}
              required
            />
            {nameWarning && (
              <div className="mt-2 flex items-center text-sm text-amber-600">
                <svg
                  className="mr-1 h-4 w-4 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>{nameWarning}</span>
              </div>
            )}
          </div>

          <div className="mb-4">
            <label
              htmlFor="email_user"
              className="mb-2 block text-sm font-medium"
            >
              Email *
            </label>
            <input
              type="email"
              name="email_user"
              id="email_user"
              placeholder="email@example.com"
              className="focus:border-transparent w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#57A863]"
              defaultValue={currentReviewer?.email_user || ""}
              required
            />
          </div>

          {/* Category Reviewer - Corrigé */}
          <div className="mb-4">
            <label className="mb-2 block text-sm font-medium text-gray-700">
              Catégorie de rélecteur *
            </label>
            <Select
              options={categories}
              isMulti={false}
              name="category"
              value={selectedCategory}
              onChange={(selected) => {
                setSelectedCategory(selected);
              }}
              placeholder="Sélectionnez une catégorie"
              classNamePrefix="select"
              noOptionsMessage={() => "Aucune catégorie disponible"}
              loadingMessage={() => "Chargement des catégories..."}
              isLoading={categories.length === 0}
              styles={{
                control: (provided, state) => ({
                  ...provided,
                  borderColor: state.isFocused ? "#57A863" : "#d1d5db",
                  boxShadow: state.isFocused
                    ? "0 0 0 2px rgba(87, 168, 99, 0.2)"
                    : "none",
                  "&:hover": {
                    borderColor: "#57A863",
                  },
                }),
                option: (provided, state) => ({
                  ...provided,
                  backgroundColor: state.isSelected
                    ? "#57A863"
                    : state.isFocused
                    ? "#f0f9ff"
                    : "white",
                  color: state.isSelected ? "white" : "#374151",
                }),
              }}
            />
            <div className="mt-1 text-xs text-gray-500">
              {selectedCategory ? "1 catégorie sélectionnée" : "Aucune catégorie sélectionnée"}
              {categories.length === 0 && (
                <span className="ml-2 text-amber-600">
                  • Chargement des catégories...
                </span>
              )}
            </div>
          </div>

          {/* Affiliations multiselect */}
          <div className="mb-4">
            <label
              htmlFor="affiliations"
              className="mb-2 block text-sm font-medium"
            >
              Affiliations *
            </label>
            <Select
              options={affiliations}
              isMulti
              name="affiliations"
              value={selectedAffiliations}
              onChange={(selected) => {
                setSelectedAffiliations(selected || []);
              }}
              placeholder="Sélectionnez une ou plusieurs affiliations"
              classNamePrefix="select"
              noOptionsMessage={() => "Aucune affiliation disponible"}
              loadingMessage={() => "Chargement des affiliations..."}
              isLoading={affiliations.length === 0}
              styles={{
                control: (provided, state) => ({
                  ...provided,
                  borderColor: state.isFocused ? "#57A863" : "#d1d5db",
                  boxShadow: state.isFocused
                    ? "0 0 0 2px rgba(87, 168, 99, 0.2)"
                    : "none",
                  "&:hover": {
                    borderColor: "#57A863",
                  },
                }),
                multiValue: (provided) => ({
                  ...provided,
                  backgroundColor: "#e7f3ff",
                }),
                multiValueLabel: (provided) => ({
                  ...provided,
                  color: "#1e40af",
                  fontSize: "0.875rem",
                }),
                multiValueRemove: (provided) => ({
                  ...provided,
                  color: "#1e40af",
                  ":hover": {
                    backgroundColor: "#1e40af",
                    color: "white",
                  },
                }),
                option: (provided, state) => ({
                  ...provided,
                  backgroundColor: state.isSelected
                    ? "#57A863"
                    : state.isFocused
                    ? "#f0f9ff"
                    : "white",
                  color: state.isSelected ? "white" : "#374151",
                }),
              }}
            />
            <div className="mt-1 text-xs text-gray-500">
              {selectedAffiliations.length} affiliation(s) sélectionnée(s)
              {affiliations.length === 0 && (
                <span className="ml-2 text-amber-600">
                  • Chargement des affiliations...
                </span>
              )}
            </div>
          </div>

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={handleClose}
              className="rounded-lg bg-gray-200 px-5 py-2.5 font-medium text-gray-700 transition-colors hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              Annuler
            </button>

            <button
              type="submit"
              className="rounded-lg bg-gradient-to-r from-[#57A863] to-[#3d9448] px-5 py-2.5 text-sm font-medium text-white shadow-md transition-all hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-[#57A863] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={isSubmitting || selectedAffiliations.length === 0 || !selectedCategory}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="mr-2 h-4 w-4 animate-spin"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Traitement...
                </span>
              ) : (
                <span className="flex items-center">
                  <MdCheckCircle className="mr-1.5 h-4 w-4" />
                  {isEditMode ? "Mettre à jour" : "Ajouter"}
                </span>
              )}
            </button>
          </div>
        </form>
      </Modals>
    </div>
  );
};

export default Utilisateurs;